/**
 * The helpdesk page.
 *
 * MKS-339 built it as the cross-project inbox of CLIENT requests, over tasks,
 * with SLA clocks. MKS-410 adds the request RECORD — internal requests too,
 * with no project until an admin routes them — so the page now picks the right
 * surface for the viewer:
 *
 *   - ADMIN → `RequestQueue`: ONE queue over the record, internal and client
 *     together, each marked by source, with routing / resolve / reject triage.
 *     This is the triage surface the ticket asks for.
 *   - EVERYONE ELSE → `ClientRequestInbox`: MKS-339's inbox, unchanged. Its
 *     list is scoped server-side to the caller's own intake-enabled projects,
 *     and the record's admin queue is `requireAdmin`, so this is both the
 *     only thing a non-admin may read and the one that still carries the SLA
 *     clocks and in-place task triage they handle requests with.
 *
 * RELEASE FLAG. Behind `mks_339_helpdesk_inbox` — the EXISTING helpdesk flag,
 * not a new one: MKS-410 extends the same dark feature. The flag-off branch is
 * the "Coming soon" placeholder this page used to be, unchanged, so production
 * keeps today's behaviour until the ticket is verified.
 *
 * `RequireFlag` is deliberately NOT used on the route: it renders a 404, and a
 * 404 on a page that is in the menu is worse than the placeholder it replaced.
 */
import { TopBar } from '../components/ui/TopBar';
import { useFlag } from '../services/flagsContext';
import { useAuth } from '../services/authContext';
import { ClientRequestInbox } from '../components/helpdesk/ClientRequestInbox';
import { RequestQueue } from '../components/helpdesk/RequestQueue';

function ComingSoon() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <p className="text-sm text-slate-400">Coming soon</p>
    </div>
  );
}

export function HelpdeskPage() {
  const helpdeskOn = useFlag('mks_339_helpdesk_inbox');
  const { isAdmin } = useAuth();

  if (!helpdeskOn) return <ComingSoon />;

  return (
    <div className="space-y-4" data-testid="helpdesk-page">
      <TopBar title="Helpdesk" />
      {isAdmin ? <RequestQueue /> : <ClientRequestInbox />}
    </div>
  );
}
